import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { X, Check } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { useAppDispatch } from "../../redux/store";
import { createServiceArea, updateServiceArea } from "../../redux/Actions/ServiceAreaAction";
import { createCountry, getAllCountries } from "../../redux/Actions/AdminAction";
import Autocomplete from "react-google-autocomplete";
import CountryDropdown from "../../components/ui/CountryDropdown";
import {
  extractLocationFromPlace,
  findOrCreateCountry,
  getProvincesByCountry,
  generateMockPostalCode,
  LocationData
} from "../../lib/locationUtils";

// Service Area Type Enum
const ServiceAreaType = {
  METROPOLITAN: 1,
  CITY: 2,
  REGION: 3,
  PROVINCE: 4
};

interface ServiceAreaModalProps {
  area?: any;
  isOpen: boolean;
  onClose: () => void;
}

const ServiceAreaModal: React.FC<ServiceAreaModalProps> = ({
  area = null,
  isOpen,
  onClose
}) => {
  const dispatch = useAppDispatch();

  const [formData, setFormData] = useState(
    area ? {
      name: area.name || "",
      type: { id: area.type?.id || ServiceAreaType.METROPOLITAN },
      country: { id: area.country?.id || "" },
      province: area.province || "",
      radius: area.radius ? area.radius.toString() : "",
      cities: Array.isArray(area.cities) ? area.cities : [],
      postalCodes: Array.isArray(area.postalCodes) ? area.postalCodes : [],
      serviceTypes: Array.isArray(area.serviceTypes) ? area.serviceTypes : [],
      emergencyServices: area.emergencyServices !== undefined ? area.emergencyServices : false, // Changed to false by default
      restrictions: Array.isArray(area.restrictions) ? area.restrictions : [],
      latitude: area.latitude || "",
      longitude: area.longitude || ""
    } : {
      name: "",
      type: { id: ServiceAreaType.METROPOLITAN },
      country: { id: "" },
      province: "",
      radius: "",
      cities: [],
      postalCodes: [],
      serviceTypes: ["General services"], // Added default "General services" for new areas
      emergencyServices: false, // Changed to false by default
      restrictions: [],
      latitude: "",
      longitude: ""
    }
  );

  // Google Maps API key
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "YOUR_GOOGLE_MAPS_API_KEY";

  const [newCity, setNewCity] = useState("");
  const [newPostalCode, setNewPostalCode] = useState("");
  const [newServiceType, setNewServiceType] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableProvinces, setAvailableProvinces] = useState<string[]>([]);
  const [isProcessingLocation, setIsProcessingLocation] = useState(false);
  const [debugInfo, setDebugInfo] = useState<string>("");
  const [isCreatingCountry, setIsCreatingCountry] = useState(false);
  const [postalCodeNotification, setPostalCodeNotification] = useState<{message: string, codes: string[]} | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<any>(null);

  // Handle place selection from Google Autocomplete
  const handlePlaceSelect = async (place: any) => {
    if (place) {
      setIsProcessingLocation(true);
      try {
        // Extract location data using utility function
        const locationData: LocationData = extractLocationFromPlace(place);

        setDebugInfo(`Extracted: Country=${locationData.country}, Province=${locationData.province}, City=${locationData.city}, Postal=${locationData.postalCode}`);

        // Find matching country from the dropdown data
        let matchedCountry = null;
        if (locationData.country) {
          // We need to access the countries from the CountryDropdown component
          // Since we can't directly access it, we'll trigger a search to find the country
          try {
            setIsCreatingCountry(true);
            
            // Dispatch an action to get countries and find the matching one
            const searchResult = await dispatch(getAllCountries({
              page: 1,
              limit: 10,
              search: locationData.country,
              isShowLoader: false
            }));

            if (searchResult.meta.requestStatus === "fulfilled" && searchResult.payload) {
              const countries = searchResult.payload.data || [];
              
              // Try to find exact match first
              matchedCountry = countries.find((country: any) => 
                country.name.toLowerCase() === locationData.country.toLowerCase()
              );
              
              // If no exact match, try partial match
              if (!matchedCountry) {
                matchedCountry = countries.find((country: any) => 
                  country.name.toLowerCase().includes(locationData.country.toLowerCase()) ||
                  locationData.country.toLowerCase().includes(country.name.toLowerCase())
                );
              }
              
              if (matchedCountry) {
                console.log("Found matching country:", matchedCountry);
                setSelectedCountry(matchedCountry);
                setFormData(prev => ({
                  ...prev,
                  country: { id: matchedCountry.id }
                }));
              }
            }
          } catch (error) {
            console.error("Error finding country:", error);
          } finally {
            setIsCreatingCountry(false);
          }
        }

        // Get available provinces for the country
        const provinces = getProvincesByCountry(locationData.country);
        setAvailableProvinces(provinces);

        // Prepare cities and postal codes arrays
        const cities = locationData.city ? [locationData.city] : [];

        // Handle postal code - use extracted or generate fallback
        let postalCodes = [];
        if (locationData.postalCode) {
          postalCodes = [locationData.postalCode];
        } else if (locationData.latitude && locationData.longitude && locationData.country) {
          // Generate a postal code using locationUtils
          const postalCode = generateMockPostalCode(
            locationData.latitude,
            locationData.longitude,
            locationData.country,
            locationData.city
          );
          if (postalCode) {
            postalCodes = [postalCode];
          }
        }

        // Update form data with all extracted information
        setFormData(prev => ({
          ...prev,
          name: place.formatted_address || "",
          country: { id: matchedCountry?.id || "" },
          province: locationData.province || "",
          cities: cities,
          postalCodes: postalCodes,
          latitude: locationData.latitude,
          longitude: locationData.longitude
        }));

        // If we found a matching country, update the selected country
        if (matchedCountry) {
          console.log("Auto-selected country:", matchedCountry.name);
        } else {
          console.log("No matching country found for:", locationData.country);
        }

      } catch (error) {
        console.error("Error processing place selection:", error);
      } finally {
        setIsProcessingLocation(false);
      }
    }
  };

  // Update form data when area prop changes
  useEffect(() => {
    if (area) {
      setFormData({
        name: area.name || "",
        type: { id: area.type?.id || ServiceAreaType.METROPOLITAN },
        country: { id: area.country?.id || "" },
        province: area.province || "",
        radius: area.radius ? area.radius.toString() : "",
        cities: Array.isArray(area.cities) ? area.cities : [],
        postalCodes: Array.isArray(area.postalCodes) ? area.postalCodes : [],
        serviceTypes: Array.isArray(area.serviceTypes) ? area.serviceTypes : [],
        emergencyServices: area.emergencyServices !== undefined ? area.emergencyServices : false, // Changed to false by default
        restrictions: Array.isArray(area.restrictions) ? area.restrictions : [],
        latitude: area.latitude || "",
        longitude: area.longitude || ""
      });

      // Set selected country for CountryDropdown
      if (area.country) {
        setSelectedCountry({
          id: area.country.id,
          name: area.country.name,
          code: area.country.code || ""
        });
      }

      // Set available provinces for the country
      if (area.country?.name) {
        const provinces = getProvincesByCountry(area.country.name);
        setAvailableProvinces(provinces);
      }
    } else {
      setFormData({
        name: "",
        type: { id: ServiceAreaType.METROPOLITAN },
        country: { id: "" },
        province: "",
        radius: "",
        cities: [],
        postalCodes: [],
        serviceTypes: ["General services"], // Added default "General services" for new areas
        emergencyServices: false, // Changed to false by default
        restrictions: [],
        latitude: "",
        longitude: ""
      });
      setSelectedCountry(null);
      setAvailableProvinces([]);
    }
  }, [area]);

  // Handle country selection from CountryDropdown
  const handleCountrySelect = (country: any) => {
    console.log("Country selected:", country);
    setSelectedCountry(country);
    if (country) {
      setFormData(prev => {
        console.log("Updating form data with country:", country);
        return {
          ...prev,
          country: { id: country.id }
        };
      });
      
      // Update available provinces for the selected country
      const provinces = getProvincesByCountry(country.name);
      setAvailableProvinces(provinces);
    } else {
      setFormData(prev => ({
        ...prev,
        country: { id: "" }
      }));
      setAvailableProvinces([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate country is selected
      if (!selectedCountry || !selectedCountry.id) {
        alert("Please select a country");
        setIsSubmitting(false);
        return;
      }

      // Validate radius is a positive number
      const radiusValue = parseInt(formData.radius);
      if (isNaN(radiusValue) || radiusValue < 0) {
        alert("Please enter a valid positive number for radius");
        setIsSubmitting(false);
        return;
      }

      const payload = {
        name: formData.name,
        type: { id: parseInt(formData.type.id) },
        radius: radiusValue,
        restrictions: formData.restrictions,
        cities: formData.cities,
        postalCodes: formData.postalCodes,
        country: { id: selectedCountry.id },
        serviceTypes: formData.serviceTypes,
        emergencyServices: formData.emergencyServices,
        province: formData.province,
        latitude: formData.latitude,
        longitude: formData.longitude
      };

      console.log("Submitting payload:", payload);
      console.log("Selected country:", selectedCountry);

      if (area) {
        // Update existing service area
        const updateResult = await dispatch(updateServiceArea({ id: area.id, data: payload }));
        if (updateResult.meta.requestStatus === "rejected") {
          // If there's an error, don't close the modal
          setIsSubmitting(false);
          return;
        }
      } else {
        // Create new service area
        const createResult = await dispatch(createServiceArea({ data: payload }));
        if (createResult.meta.requestStatus === "rejected") {
          // If there's an error, don't close the modal
          setIsSubmitting(false);
          return;
        }
      }

      // Only close the modal if the operation was successful
      setIsSubmitting(false);
      onClose();
    } catch (error) {
      setIsSubmitting(false);
      // Don't close the modal on error
    }
  };

  const addCity = () => {
    if (newCity.trim()) {
      setFormData({
        ...formData,
        cities: [...formData.cities, newCity.trim()]
      });
      setNewCity("");
    }
  };

  const addPostalCode = () => {
    if (newPostalCode.trim()) {
      setFormData({
        ...formData,
        postalCodes: [...formData.postalCodes, newPostalCode.trim()]
      });
      setNewPostalCode("");
    }
  };

  const addServiceType = () => {
    if (newServiceType.trim()) {
      setFormData({
        ...formData,
        serviceTypes: [...formData.serviceTypes, newServiceType.trim()]
      });
      setNewServiceType("");
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        // Prevent closing when clicking backdrop during country creation or location processing
        if (isCreatingCountry || isProcessingLocation) {
          e.stopPropagation();
          return;
        }
        // Only close if clicking the backdrop itself, not the modal content
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <motion.div
        initial={{ scale: 0.95 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.95 }}
        className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-6 border-b sticky top-0 bg-white" style={{ textAlign: "left" }}>
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">
              {area ? "Edit Service Area" : "Add New Service Area"}
            </h2>
            <button
              onClick={onClose}
              disabled={isCreatingCountry || isProcessingLocation}
              className={`p-2 rounded-full ${isCreatingCountry || isProcessingLocation
                  ? "bg-gray-100 cursor-not-allowed opacity-50"
                  : "hover:bg-gray-100"
                }`}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6" style={{ textAlign: "left" }}>
          {/* Basic Information */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Area Name</label>
              {(isProcessingLocation || isCreatingCountry) && (
                <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-xs text-blue-700">
                    {isCreatingCountry ? "🔄 Creating new country..." : "🔄 Processing location data..."}
                  </p>
                </div>
              )}
              <div className="relative" style={{ position: "relative", zIndex: 10 }}>
                <Autocomplete
                  apiKey={GOOGLE_MAPS_API_KEY}
                  onPlaceSelected={handlePlaceSelect}
                  options={{
                    types: ["geocode", "establishment"],
                    componentRestrictions: { country: [] },
                    fields: ["address_components", "formatted_address", "geometry", "name"]
                  }}
                  value={formData.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Search for area name..."
                  className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  style={{
                    position: "relative",
                    zIndex: 1
                  }}
                  required
                />
                {(isProcessingLocation || isCreatingCountry) && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  </div>
                )}
              </div>
            </div>

            {/* Coordinates Display */}
            {(formData.latitude || formData.longitude) && (
              <div className="bg-gray-50 p-3 rounded-lg space-y-2">
                <h4 className="text-sm font-medium text-gray-700">Coordinates:</h4>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Latitude:</span> {formData.latitude?.toFixed(6) || "Not set"}
                </div>
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Longitude:</span> {formData.longitude?.toFixed(6) || "Not set"}
                </div>
                {formData.postalCodes.length > 0 && (
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Postal Codes:</span> {formData.postalCodes.join(", ")}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Type</label>
                <select
                  value={formData.type.id}
                  onChange={(e) => setFormData({ ...formData, type: { id: parseInt(e.target.value) } })}
                  className="mt-1 w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value={ServiceAreaType.METROPOLITAN}>Metropolitan Area</option>
                  <option value={ServiceAreaType.CITY}>City</option>
                  <option value={ServiceAreaType.REGION}>Region</option>
                  <option value={ServiceAreaType.PROVINCE}>Province</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Country</label>
                <div className="mt-1">
                  <CountryDropdown
                    selectedCountry={selectedCountry}
                    onCountrySelect={handleCountrySelect}
                    placeholder="Select Country"
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Province/State</label>
              <Input
                type="text"
                value={formData.province}
                onChange={(e) => setFormData({ ...formData, province: e.target.value })}
                placeholder="Enter province or state"
                className="mt-1"
                required
              />
              {availableProvinces.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs text-gray-500 mb-1">Common provinces/states for this country:</p>
                  <div className="flex flex-wrap gap-1">
                    {availableProvinces.slice(0, 5).map((province, index) => (
                      <button
                        key={index}
                        type="button"
                        onClick={() => setFormData({ ...formData, province })}
                        className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        {province}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Coverage */}
          <div className="space-y-4">
            <h3 className="font-medium">Coverage Details</h3>

            <div>
              <label className="block text-sm font-medium text-gray-700">Coverage Radius (km)</label>
              <Input
                type="number"
                min="0"
                step="1"
                value={formData.radius}
                onChange={(e) => setFormData({ ...formData, radius: e.target.value })}
                placeholder="Enter radius"
                className="mt-1"
                required
              />
            </div>

            {/* Cities */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Cities
              </label>
              <div className="flex gap-2 mt-1">
                <div className="relative flex-1" style={{ position: "relative", zIndex: 10 }}>
                  <Autocomplete
                    apiKey={GOOGLE_MAPS_API_KEY}
                    onPlaceSelected={async (place) => {
                      if (place) {
                        
                        // Extract city name from the selected place
                        let cityName = "";
                        let cityPostalCodes: string[] = [];
                        
                        // Try to get city from address components
                        if (place.address_components) {
                          const cityComponent = place.address_components.find(
                            component => 
                              component.types.includes("locality") || 
                              component.types.includes("administrative_area_level_2")
                          );
                          if (cityComponent) {
                            cityName = cityComponent.long_name;
                          }
                        }
                        
                        // Fallback to formatted address if no city component found
                        if (!cityName && place.formatted_address) {
                          // Extract city from formatted address (first part before comma)
                          cityName = place.formatted_address.split(",")[0].trim();
                        }
                        
                        if (cityName && !formData.cities.includes(cityName)) {
                          // First, try to extract postal codes directly from the selected place
                          if (place.address_components) {
                            place.address_components.forEach((component) => {
                              if (component.types.includes("postal_code") || 
                                  component.types.includes("postal_code_prefix")) {
                                cityPostalCodes.push(component.long_name);
                              }
                            });
                          }
                          
                          // If no postal codes found in the place, try to get postal code from locationUtils
                          if (cityPostalCodes.length === 0 && place.geometry?.location) {
                            const location = place.geometry.location;
                            const lat = typeof location.lat === "function" ? location.lat() : location.lat;
                            const lng = typeof location.lng === "function" ? location.lng() : location.lng;
                            const countryName = selectedCountry?.name || "";
                            
                            // Use locationUtils to get postal code
                            const postalCode = generateMockPostalCode(lat, lng, countryName, cityName);
                            if (postalCode) {
                              cityPostalCodes = [postalCode];
                            }
                          }
                          
                          // Add city to the list
                          setFormData({
                            ...formData,
                            cities: [...formData.cities, cityName]
                          });
                          
                          // Add postal codes to the postal codes list
                          if (cityPostalCodes.length > 0) {
                            const existingPostalCodes = new Set(formData.postalCodes);
                            const newPostalCodes = cityPostalCodes.filter(code => !existingPostalCodes.has(code));
                            
                            if (newPostalCodes.length > 0) {
                              setFormData(prev => ({
                                ...prev,
                                postalCodes: [...prev.postalCodes, ...newPostalCodes]
                              }));
                            }
                          }
                          
                          // Clear the input field after selection
                          setNewCity("");
                        }
                      }
                    }}
                    options={{
                      types: ["(cities)"],
                      componentRestrictions: { country: [] },
                      fields: ["address_components", "formatted_address", "name", "geometry"]
                    }}
                    value={newCity}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewCity(e.target.value)}
                    placeholder="Search and select city..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    style={{
                      position: "relative",
                      zIndex: 1
                    }}
                  />
                </div>
                <Button
                  type="button"
                  onClick={addCity}
                  variant="outline"
                >
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.cities.map((city, index) => (
                  <Badge variant={undefined}
                    key={index}
                    className="bg-blue-100 text-blue-700 flex items-center gap-1"
                  >
                    {city}
                    <button
                      type="button"
                      onClick={() => setFormData({
                        ...formData,
                        cities: formData.cities.filter((_, i) => i !== index)
                      })}
                      className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            {/* Postal Codes */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Postal Codes
              </label>
              
              {/* Notification for auto-added postal codes */}
              {postalCodeNotification && (
                <div className="mb-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Check className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-800">
                      {postalCodeNotification.message}
                    </span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {postalCodeNotification.codes.map((code, index) => (
                      <Badge key={index} variant={undefined} className="bg-green-100 text-green-700 text-xs">
                        {code}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 mt-1">
                <Input
                  value={newPostalCode}
                  onChange={(e) => setNewPostalCode(e.target.value)}
                  placeholder="Add additional postal code"
                />
                <Button
                  type="button"
                  onClick={addPostalCode}
                  variant="outline"
                >
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.postalCodes.map((code, index) => (
                  <Badge variant={undefined}
                    key={index}
                    className="bg-purple-100 text-purple-700 flex items-center gap-1"
                  >
                    {code}
                    <button
                      type="button"
                      onClick={() => setFormData({
                        ...formData,
                        postalCodes: formData.postalCodes.filter((_, i) => i !== index)
                      })}
                      className="ml-1 hover:bg-purple-200 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {/* Service Types */}
          <div className="space-y-4">
            <h3 className="font-medium">Available Services</h3>
            <div className="flex gap-2">
              <Input
                value={newServiceType}
                onChange={(e) => setNewServiceType(e.target.value)}
                placeholder="Add service type"
              />
              <Button
                type="button"
                onClick={addServiceType}
                variant="outline"
              >
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {formData.serviceTypes.map((service, index) => (
                <Badge variant={undefined}
                  key={index}
                  className="bg-green-100 text-green-700 flex items-center gap-1"
                >
                  {service}
                  <button
                    type="button"
                    onClick={() => setFormData({
                      ...formData,
                      serviceTypes: formData.serviceTypes.filter((_, i) => i !== index)
                    })}
                    className="ml-1 hover:bg-green-200 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </div>

          {/* Settings */}
          <div className="space-y-4">
            <h3 className="font-medium">Area Settings</h3>
            <div className="flex flex-col gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.emergencyServices}
                  onChange={(e) => setFormData({ ...formData, emergencyServices: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span>Enable Emergency Services</span>
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isCreatingCountry || isProcessingLocation}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || isCreatingCountry || isProcessingLocation}
            >
              {isSubmitting ? "Saving..." :
                isCreatingCountry ? "Creating Country..." :
                  isProcessingLocation ? "Processing..." :
                    (area ? "Save Changes" : "Create Area")}
            </Button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default ServiceAreaModal;
